<script lang="ts" setup>
import { CART_REQUEST_ORDER_KEY } from '~/api/cart';
import request from '~/api/utils';

const { t, locale } = useI18n();
const userStore = useUserStore();
const orderStore = useOrderStore();
const productCartStore = useProductCartStore();
const subscribeProductsStore = useSubscribeProductsStore();
const dataLayer = useDataLayer();

provide(INJECTION_KEYS.AuthorizationTheme, ThemeEnum.light);

const activeTabIndex = computed(() => {
  if (!userStore.authorized) return 0;
  return 1;
});

const synchronizeCartData = () => orderStore.fetchCurrentOrder();

const onCompleted = () => {
  Promise.all([
    productCartStore.transferCartData(),
    subscribeProductsStore.transfer(),
  ]).finally(() => {
    orderStore.fetchCurrentOrder();
  });
};

const sendGTMCheckoutEventWithStep = (step: number) =>
  dataLayer.append(
    {
      event: 'checkout',
      ecommerce: {
        checkout: {
          actionField: { step },
          products: orderStore.cartData.map((cartItem) => ({
            id: cartItem.productId,
            name: cartItem.name,
            price: cartItem.price,
            quantity: cartItem.quantity,
          })),
        },
      },
    },
    ['user'],
  );

watch(
  () => userStore.user,
  (v) => {
    if (v) {
      request.hooks.subscribe(
        `order:${CART_REQUEST_ORDER_KEY}:finish`,
        synchronizeCartData,
      );
    }
  },
  { immediate: true },
);

watch(
  () => !!orderStore.delivery.current,
  (v) => {
    if (v) sendGTMCheckoutEventWithStep(1);
  },
  { immediate: true },
);

watch(
  () => !!orderStore.payment.current,
  (v) => {
    if (v) sendGTMCheckoutEventWithStep(2);
  },
  { immediate: true },
);

onMounted(() => {
  if (userStore.authorized) orderStore.fetchCurrentOrder();
});

onBeforeUnmount(() => {
  request.hooks.unsubscribe(
    `order:${CART_REQUEST_ORDER_KEY}:finish`,
    synchronizeCartData,
  );
});

useHead({
  script: [
    {
      src: `https://api-maps.yandex.ru/2.1/?lang=${locale.value === 'ru' ? 'ru_RU' : 'en_US'}&apikey=0dec5f79-9566-4a74-a6f5-1cbcdbdfa2b5`,
      type: 'text/javascript',
      key: 'yandex-maps',
      async: true,
      tagPosition: 'bodyClose',
    },
  ],
});
</script>

<template>
  <PaymentProvider is="main" class="orderPage no-md-footer">
    <Head>
      <Title>{{ t('title') }}</Title>
      <Meta property="og:title" :content="t('title')" />
    </Head>

    <nav
      class="orderPage__header"
      :class="`orderPage__header_${activeTabIndex + 1}`"
    >
      <span class="orderPage__state">{{ t('state.0') }}</span>
      <span class="orderPage__state">{{ t('state.1') }}</span>
      <span class="orderPage__state">{{ t('state.2') }}</span>
    </nav>

    <div class="orderPage__details">
      <OrderCreatingLayout v-if="userStore.authorized" />
      <AuthFullFlow
        v-else
        @completed="onCompleted"
        :title="t('auth_title')"
        title-class="orderPage__authFlowTitle"
        class="orderPage__authFlow"
      />
    </div>

    <OrderProductCart class="orderPage__cart" />
  </PaymentProvider>
</template>

<i18n>
ru:
  title: Оформление заказа
  state:
    - Авторизация
    - Доставка
    - Оплата
  auth_title: Чтобы оформить заказ, необходимо войти или зарегистрироваться

en:
  title: Order placement
  state:
    - Authorization
    - Delivery
    - Payment
  auth_title: To place an order, you need to log in or register
</i18n>

<style lang="scss">
.orderPage {
  $self: &;

  display: flex;

  @include mq('md') {
    min-height: 100svh;
    flex-direction: column;
    padding-bottom: 46px;
    background-color: #f2f2f2;
  }

  &__header {
    display: none;
    background-color: #fff;
    position: relative;
    border-bottom: 1px solid #e6e6e6;
    padding-top: 48px;

    @include mq('md') {
      display: flex;
    }

    &::after {
      content: '';
      height: 1px;
      width: calc(100% / 3);
      background-color: #121212;
      transition: transform 0.3s ease;
      left: 0;
      bottom: 0;
      pointer-events: none;
      position: absolute;
    }

    &_1 #{$self}__state:nth-child(1) ~ * {
      color: #c0c0c0;
    }

    &_2 {
      &::after {
        transform: translateX(100%);
      }

      #{$self}__state:nth-child(2) ~ * {
        color: #c0c0c0;
      }
    }

    &_3::after {
      transform: translateX(200%);
    }
  }

  &__state {
    padding: 14px 0;
    font-size: 12px;
    line-height: 120%;
    display: flex;
    align-items: center;
    justify-content: center;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #121212;
    width: calc(100% / 3);
    transition: color 0.3s ease;
  }

  & &__details {
    background-color: #f2f2f2;
    width: 50%;
    min-height: 100%;
    padding: 100px 0;

    @include mq('md') {
      min-height: initial;
      width: 100%;
      padding: 0;
    }
  }

  &__authFlow {
    @include mq('sm') {
      padding-top: 64px;
    }
  }

  & &__authFlowTitle {
    @include mq('sm') {
      font-size: 24px;
      line-height: 32px;
      margin-bottom: 32px;
    }
  }

  &__cart {
    width: 50%;

    @include mq('md') {
      width: 100%;
    }
  }
}
</style>
